import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { musicApi, errMsg, fmtTime } from "@/lib/musicApi";

const Card = ({ title, children, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={testId}><h3 className="mb-4 text-sm font-black text-white">{title}</h3>{children}</div>
);

// Admin: usage, cost, errors, model configuration and rights-holder reports.
export const MusicInsights = ({ config, onSettings }) => {
  const [stats, setStats] = useState(null);
  const [s, setS] = useState(config.settings);
  const [tds, setTds] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    musicApi.stats().then(setStats).catch(() => toast.error("Could not load stats"));
    musicApi.takedowns().then(setTds).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try { const r = await musicApi.saveSettings(s); setS(r); onSettings(r); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const setTd = async (id, status) => {
    try { await musicApi.setTakedown(id, status); setTds((l) => l.map((x) => (x.id === id ? { ...x, status } : x))); } catch (e) { toast.error(errMsg(e)); }
  };
  const num = (k, label, min, max) => (
    <label className="text-[11px] font-semibold text-white/50">{label}
      <input type="number" min={min} max={max} value={s[k]} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} data-testid={`setting-${k}`}
        className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-sm text-white outline-none focus:border-[#C8FF4D]/60" />
    </label>
  );

  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="music-insights">
      <Card title="Usage & cost by engine" testId="music-stats">
        {!stats ? <Loader2 className="animate-spin text-[#C8FF4D]" /> : (
          <table className="w-full text-left text-xs">
            <thead className="text-white/40"><tr><th className="pb-2">Engine</th><th>Runs</th><th>Done</th><th>Failed</th><th>Audio</th><th>Cost</th><th>$/min</th><th>Speed</th></tr></thead>
            <tbody className="font-mono text-white/80">
              {Object.entries(stats.providers).map(([k, p]) => (
                <tr key={k} className="border-t border-white/5" data-testid={`stats-row-${k}`}>
                  <td className="py-2 font-ui font-semibold">{config.providers.find((x) => x.key === k)?.label || k}</td>
                  <td>{p.total}</td><td>{p.done}</td><td className={p.failed ? "text-red-300" : ""}>{p.failed}</td><td>{fmtTime(p.seconds)}</td>
                  <td>${p.cost_usd.toFixed(2)}</td><td>{p.usd_per_minute != null ? `$${p.usd_per_minute}` : "—"}</td><td>{p.sec_per_audio_sec != null ? `${p.sec_per_audio_sec}s/s` : "—"}</td>
                </tr>
              ))}
              {!Object.keys(stats.providers).length && <tr><td colSpan={8} className="py-4 font-ui text-white/40">No generations yet.</td></tr>}
            </tbody>
          </table>
        )}
        <p className="mt-3 text-[11px] leading-relaxed text-white/35">Costs are estimates from published per-minute / per-track prices; Universal Key bills the exact provider cost. ElevenLabs (your key) is billed to your ElevenLabs plan.</p>
      </Card>
      <Card title="Limits & model configuration" testId="music-settings">
        <div className="grid grid-cols-2 gap-3">
          {num("daily_limit", "Generations per day", 1, 1000)}{num("max_concurrent", "Parallel jobs per user", 1, 10)}
          {num("max_duration", "Max track length (s)", 10, 360)}{num("max_upload_mb", "Max upload (MB)", 5, 200)}
          {num("max_reference_seconds", "Reference audio used (s)", 30, 1800)}
          <label className="text-[11px] font-semibold text-white/50">Default engine
            <select value={s.default_provider} onChange={(e) => setS({ ...s, default_provider: e.target.value })} data-testid="setting-default_provider"
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white">{config.providers.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</select>
          </label>
        </div>
        <button onClick={save} disabled={busy} data-testid="music-settings-save" className="mt-4 flex items-center gap-1.5 rounded-full bg-[#C8FF4D] px-4 py-2 text-xs font-black text-black disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</button>
      </Card>
      <Card title="Recent errors" testId="music-errors">
        {(stats?.recent_errors || []).map((e, i) => (
          <div key={i} className="border-t border-white/5 py-2 text-xs"><div className="font-semibold text-white">{e.title} <span className="text-white/35">· {e.provider} · {new Date(e.created_at).toLocaleString()}</span></div><div className="text-red-300/80">{e.error}</div></div>
        ))}
        {stats && !stats.recent_errors.length && <p className="text-xs text-white/40">No errors.</p>}
      </Card>
      <Card title={`Rights-holder reports (${tds.filter((x) => x.status === "open").length} open)`} testId="music-takedowns">
        <p className="mb-3 text-[11px] text-white/40">Public form: <a href="/music-takedown" className="text-[#7AD7FF] hover:underline">/music-takedown</a></p>
        {tds.map((d) => (
          <div key={d.id} className="border-t border-white/5 py-2 text-xs" data-testid={`takedown-${d.id}`}>
            <div className="flex items-center justify-between gap-2"><span className="font-semibold text-white">{d.work}</span><span className="rounded-full bg-white/10 px-2 text-[10px] uppercase text-white/60">{d.status}</span></div>
            <div className="text-white/45">{d.name} · {d.email} · {d.track_reference}</div>
            <p className="mt-1 text-white/70">{d.details}</p>
            <div className="mt-1.5 flex gap-2">{["removed", "rejected", "open"].filter((x) => x !== d.status).map((st) => <button key={st} onClick={() => setTd(d.id, st)} data-testid={`takedown-${d.id}-${st}`} className="text-[11px] font-semibold text-[#7AD7FF] hover:underline">Mark {st}</button>)}</div>
          </div>
        ))}
        {!tds.length && <p className="text-xs text-white/40">No reports.</p>}
      </Card>
    </div>
  );
};
